import AsyncStorage from "@react-native-async-storage/async-storage";
import { create, StateCreator } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";

// Standalone mobile store for the What's New modal. Mirrors the shape of the
// web store (src/modules/whats-new/state.ts) but persists via AsyncStorage
// instead of localStorage, and shares nothing with it.

interface WhatsNewState {
  hasDismissedServiceLinesIntro: boolean;
  // AsyncStorage rehydrates asynchronously on RN (web's localStorage is
  // synchronous), so `hasDismissedServiceLinesIntro` reads as its initial
  // `false` until rehydration finishes. The gate waits on `hasHydrated` so an
  // already-dismissed user doesn't briefly see the modal flash open on launch.
  hasHydrated: boolean;
  dismiss: () => void;
  reset: () => void;
  setHasHydrated: (value: boolean) => void;
}

type WhatsNewPersist = (
  config: StateCreator<WhatsNewState>,
  options: PersistOptions<
    WhatsNewState,
    Pick<WhatsNewState, "hasDismissedServiceLinesIntro">
  >
) => StateCreator<WhatsNewState>;

const initialState = {
  hasDismissedServiceLinesIntro: false,
};

const useWhatsNewStore = create<WhatsNewState>(
  (persist as unknown as WhatsNewPersist)(
    (set) => ({
      ...initialState,
      hasHydrated: false,
      dismiss: () => set({ hasDismissedServiceLinesIntro: true }),
      reset: () => set(initialState),
      setHasHydrated: (value) => set({ hasHydrated: value }),
    }),
    {
      name: "whats-new-state-v1",
      getStorage: () => AsyncStorage,
      // Persist only the dismissal; `hasHydrated` stays transient so it always
      // starts `false` on a cold launch and flips true once rehydration runs.
      partialize: (state) => ({
        hasDismissedServiceLinesIntro: state.hasDismissedServiceLinesIntro,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

export { useWhatsNewStore };
export default useWhatsNewStore;
